/**
 * PlaybookTab — the network-level, READ-ONLY view of a project network's frozen playbook
 * (PROJECT-NETWORKS.md §4). It shows the WHOLE plan the playbook factory groomed and froze
 * into the active manifest, so the user can review/validate it and decide whether to groom:
 *
 *   1. Milestones   — the phase spine (DISCOVERY → … → HANDOVER), each a collapsible GROUP
 *                     header (exit criterion + gate), expandable to its task rows.
 *   2. Task DAG     — a TABLE per milestone: Task · Lead · Team · Files (in→out) · Depends ·
 *                     Wave — so the detail spreads across columns instead of piling on the left.
 *   3. Agent mandates — the per-agent charter merged across milestones (mandate + workdir +
 *                     who they follow).
 *
 * Nothing here EDITS the plan. To change it, the user grooms via the Build tab's
 * "Revise playbook only" affordance — this tab links there (``onRevisePlaybook``). Data arrives
 * on ``network.playbook`` (network GET sibling) — no fetch here.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ListChecks, Wand2, ArrowRight } from 'lucide-react';

// The frozen playbook is delivered on the network object as a GET sibling. Accept either the
// (current) ``network.playbook`` or a legacy ``network.latestBuild.playbook`` just in case.
function pickPlaybook(network) {
  const pb = network?.playbook;
  if (pb && typeof pb === 'object' && Array.isArray(pb.milestones)) return pb;
  const lb = network?.latestBuild?.playbook;
  if (lb && typeof lb === 'object' && Array.isArray(lb.milestones)) return lb;
  return null;
}

// Group tasks by their owning milestone id (frozen tasks key milestone by id, e.g. "M0").
function tasksByMilestone(tasks) {
  const out = {};
  for (const t of Array.isArray(tasks) ? tasks : []) {
    if (t && typeof t === 'object' && t.milestone) {
      (out[t.milestone] = out[t.milestone] || []).push(t);
    }
  }
  return out;
}

// A cell listing files (input or output) as small chips; renders an em-dash when empty.
function FileList({ files }) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (list.length === 0) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((f, i) => (
        <span key={`${f}-${i}`} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600" title={f}>
          {f}
        </span>
      ))}
    </div>
  );
}

function AgentChips({ agents }) {
  const list = Array.isArray(agents) ? agents.filter(Boolean) : [];
  if (list.length === 0) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((a) => (
        <span key={a} className="rounded bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-600 ring-1 ring-slate-200">{a}</span>
      ))}
    </div>
  );
}

// One task = a TWO-DECKER row. The TOP deck holds only the task ID plus the structured columns
// (lead · team · files · deps · wave) — kept narrow. The LOWER deck is a single merged (colSpan)
// cell holding the TITLE + free-text DESCRIPTION, so the wordy content stretches the full table
// width instead of crushing into the narrow first column.
function TaskRow({ task }) {
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn.filter(Boolean) : [];
  const hasDesc = !!(task.description && String(task.description).trim());
  return (
    <>
      <tr className="align-top border-t border-gray-100 hover:bg-gray-50/60">
        <td className="w-px whitespace-nowrap px-3 py-2">
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{task.id}</span>
        </td>
        <td className="px-3 py-2">
          {task.leadAgent
            ? <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-blue-200">{task.leadAgent}</span>
            : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-3 py-2"><AgentChips agents={task.involvedAgents} /></td>
        <td className="px-3 py-2"><FileList files={task.inputFiles} /></td>
        <td className="px-3 py-2"><FileList files={task.outputFiles} /></td>
        <td className="px-3 py-2">
          {deps.length > 0
            ? <div className="flex flex-wrap gap-1">{deps.map((d) => (
                <span key={d} className="rounded bg-gray-50 px-1 py-0.5 text-[10px] font-medium text-gray-500">{d}</span>
              ))}</div>
            : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-3 py-2 text-center text-[11px] text-gray-500">
          {Number.isFinite(task.wave) ? task.wave : '—'}
        </td>
      </tr>
      {/* LOWER DECK — merged across all columns: the TITLE (bold) + the description prose beneath
          it. A bottom border closes the pair so the two rows read as ONE task. Indented +
          left-accented to tie it to its ID above. */}
      <tr className="border-b border-gray-200 hover:bg-gray-50/60">
        <td colSpan={COLS.length} className="px-3 pb-2 pl-6">
          <div className="border-l-2 border-gray-100 pl-2">
            <div className="text-xs font-medium text-gray-800">{task.title}</div>
            {hasDesc ? (
              <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500">{task.description}</p>
            ) : null}
          </div>
        </td>
      </tr>
    </>
  );
}

const COLS = [
  { key: 'task', label: 'ID', cls: 'w-px whitespace-nowrap' },
  { key: 'lead', label: 'Lead', cls: '' },
  { key: 'team', label: 'Team', cls: '' },
  { key: 'in', label: 'Inputs', cls: '' },
  { key: 'out', label: 'Outputs', cls: '' },
  { key: 'deps', label: 'Depends on', cls: '' },
  { key: 'wave', label: 'Wave', cls: 'text-center' },
];

function MilestoneSection({ milestone, tasks, concurrency, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const count = tasks.length;
  // Order tasks by dependency WAVE when the concurrency block covers this milestone (makes the
  // parallel/sequential structure legible); else keep their frozen order.
  const ordered = useMemo(() => {
    const waves = Array.isArray(concurrency?.waves) ? concurrency.waves : null;
    if (!waves) return tasks;
    const byId = {};
    for (const t of tasks) byId[t.id] = t;
    const seen = new Set();
    const out = [];
    for (const wave of waves) {
      for (const id of (Array.isArray(wave) ? wave : [])) {
        if (byId[id] && !seen.has(id)) { out.push(byId[id]); seen.add(id); }
      }
    }
    // Append any task not referenced by a wave (defensive) so nothing is dropped.
    for (const t of tasks) if (!seen.has(t.id)) out.push(t);
    return out;
  }, [tasks, concurrency]);

  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 bg-gray-50 px-3 py-2 text-left hover:bg-gray-100"
      >
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden /> : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />}
        <span className="text-sm font-semibold text-gray-800">{milestone.name}</span>
        <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-500 ring-1 ring-gray-200">{milestone.id}</span>
        <span className="text-[11px] text-gray-400">{count} task{count === 1 ? '' : 's'}</span>
        {milestone.exitCriterion ? (
          <span className="mx-2 hidden min-w-0 flex-1 truncate text-[11px] text-gray-400 sm:block" title={milestone.exitCriterion}>
            {milestone.exitCriterion}
          </span>
        ) : null}
        {milestone.gate ? (
          <span className="ml-auto shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 ring-1 ring-amber-200" title="Milestone gate">
            {milestone.gate} gate
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-t border-gray-100 bg-white">
                {COLS.map((c) => (
                  <th key={c.key} className={`px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 ${c.cls}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ordered.length > 0
                ? ordered.map((t) => <TaskRow key={t.id} task={t} />)
                : (
                  <tr><td colSpan={COLS.length} className="px-3 py-3 text-[11px] text-gray-400">No tasks in this milestone.</td></tr>
                )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function MandatesSection({ mandates }) {
  const [open, setOpen] = useState(false);
  const entries = Object.entries(mandates || {});
  if (entries.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 bg-gray-50 px-3 py-2 text-left hover:bg-gray-100"
      >
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden /> : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />}
        <span className="text-sm font-semibold text-gray-800">Agent mandates</span>
        <span className="text-[11px] text-gray-400">{entries.length} agent{entries.length === 1 ? '' : 's'}</span>
      </button>
      {open ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-t border-gray-100">
                <th className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Agent</th>
                <th className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Workdir</th>
                <th className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Follows</th>
                <th className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Mandate</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([agent, m]) => (
                <tr key={agent} className="border-t border-gray-100 align-top hover:bg-gray-50/60">
                  <td className="px-3 py-2 text-xs font-medium text-gray-800">{agent}</td>
                  <td className="px-3 py-2">
                    {m?.workdir ? <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">{m.workdir}</span> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {m?.followsGuidanceFrom
                      ? <span className="inline-flex items-center gap-1 text-[10px] text-gray-500"><ArrowRight className="h-3 w-3" aria-hidden /> {m.followsGuidanceFrom}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {m?.mandate ? <p className="max-w-xl text-[11px] leading-snug text-gray-500">{m.mandate}</p> : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export default function PlaybookTab({ network, onRevisePlaybook }) {
  const playbook = useMemo(() => pickPlaybook(network), [network]);
  const grouped = useMemo(() => tasksByMilestone(playbook?.tasks), [playbook]);
  const milestones = Array.isArray(playbook?.milestones) ? playbook.milestones : [];
  const concurrency = playbook?.concurrency && typeof playbook.concurrency === 'object' ? playbook.concurrency : {};
  const totalTasks = Array.isArray(playbook?.tasks) ? playbook.tasks.length : 0;

  // LEGACY (PRODUCT.md R2c): only networks built with build-time milestones have a playbook — new networks plan in
  // their rooms (the Plan gate). SUNSET(legacy-build-phases)
  const isProject = Array.isArray(network?.phases || network?.Phases) && (network.phases || network.Phases).length > 0;
  if (!isProject) {
    return (
      <div className="p-4 text-sm text-gray-500">
        This network has no playbook. Plans now live in rooms: open a room and choose Plan… to draft its
        milestones from what the room already knows.
      </div>
    );
  }
  // Project network, but the frozen playbook isn't available (older build, or manifest
  // unreadable). Point the user at grooming to (re)generate it.
  if (!playbook || milestones.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-auto p-4">
        <div className="rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-500">
          No frozen playbook found for this network yet. If this is an older project network,
          use{' '}
          <button
            type="button"
            onClick={onRevisePlaybook}
            className="font-medium text-blue-600 underline-offset-2 hover:underline"
          >
            Revise playbook only
          </button>{' '}
          on the Build tab to generate it.
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto p-4">
      {/* Header — the whole-plan summary + the single grooming entry point. */}
      <div className="mb-3 flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-gray-500" aria-hidden />
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-800">Project playbook</h2>
          <p className="text-[11px] text-gray-400">
            {milestones.length} milestone{milestones.length === 1 ? '' : 's'} · {totalTasks} task{totalTasks === 1 ? '' : 's'}
            {playbook.version ? ` · v${playbook.version}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onRevisePlaybook}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
          title="Groom the plan via the Build tab’s ‘Revise playbook only’ affordance"
        >
          <Wand2 className="h-3.5 w-3.5" aria-hidden />
          Revise playbook
        </button>
      </div>

      <div className="space-y-2">
        {milestones.map((m, i) => (
          <MilestoneSection
            key={m.id || i}
            milestone={m}
            tasks={grouped[m.id] || []}
            concurrency={concurrency[m.id]}
            defaultOpen={i === 0}
          />
        ))}
        <MandatesSection mandates={playbook.agentMandates} />
      </div>
    </div>
  );
}
